import React from "react";
import PropTypes from "prop-types";
import Text from "@Components/Text";
import { Dropdown } from "semantic-ui-react";
import styles from "./styles.module.css";

// Función auxiliar para convertir un nombre de color a hexadecimal
const getHexColor = (color) => {
	if (!color) return null;
	if (color.startsWith("#")) return color;

	const tempElem = document.createElement("div");
	tempElem.style.color = color;
	document.body.appendChild(tempElem);
	const computedColor = window.getComputedStyle(tempElem).color;
	document.body.removeChild(tempElem);

	if (computedColor.startsWith("rgb")) {
		const [r, g, b] = computedColor.match(/\d+/g).map(Number);
		return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
	}
	return "#000000"; // Valor por defecto
};

// Función para determinar si un color es claro o oscuro
const isLightColor = (color) => {
	const hexColor = getHexColor(color);
	if (!hexColor) return false;
	const r = parseInt(hexColor.slice(1, 3), 16);
	const g = parseInt(hexColor.slice(3, 5), 16);
	const b = parseInt(hexColor.slice(5, 7), 16);
	// Fórmula para luminosidad: 0.299*R + 0.587*G + 0.114*B
	const brightness = (r * 299 + g * 587 + b * 114) / 1000;
	return brightness > 155;
};

const ColorPickerDropdown = ({
	options,
	currentColor,
	setCurrentColor,
	selectedColors,
	setSelectedColors,
	isMultiple = false,
	disabled = false,
	label = "Color:",
	direction = "upward",
}) => {
	// Devuelve el valor a utilizar según el tipo (string o array) y el modo (múltiple o único)
	const computeEffectiveValue = () => {
		if (isMultiple) {
			return Array.isArray(selectedColors) ? selectedColors : selectedColors;
		}
		if (typeof selectedColors === "string") return selectedColors;
		if (Array.isArray(selectedColors) && selectedColors.length > 0) return selectedColors[0];
		return currentColor || "";
	};

	const effectiveValue = computeEffectiveValue();

	const handleChange = (e, { value }) => {
		if (isMultiple) {
			if (setSelectedColors) {
				setSelectedColors(value);
			}
			return;
		}

		if (typeof selectedColors === "string") {
			if (setSelectedColors) {
				setSelectedColors(value);
			}
		} else if (setSelectedColors) {
			setSelectedColors(value ? [value] : []);
		}
		if (setCurrentColor) {
			setCurrentColor(value);
		}
	};

	const colorOptions = options.map(({ name, label: optionLabel, color }) => {
		const backgroundColor = color || name;
		const textColor = isLightColor(backgroundColor) ? "black" : "white";
		return {
			key: name,
			text: optionLabel,
			value: name,
			content: (
				<div
					style={{
						backgroundColor,
						color: textColor,
						padding: "10px",
						margin: "0",
						borderRadius: "5px",
					}}>
					{optionLabel}
				</div>
			),
		};
	});

	const getSelectedColor = () => {
		if (!isMultiple && effectiveValue) {
			const foundOption = options.find((opt) => {
				return opt.name === effectiveValue;
			});
			return foundOption ? foundOption.color || foundOption.name : effectiveValue;
		}
		return undefined;
	};

	const selectedColor = getSelectedColor();

	const singleModeStyles =
		!isMultiple && selectedColor
			? {
					backgroundColor: selectedColor,
					color: isLightColor(selectedColor) ? "black" : "white",
			  }
			: {};

	// Mapa de opciones para renderLabel en modo múltiple
	const optionsMap = new Map(
		options.map((option) => {
			return [option.name, { color: option.color || option.name, label: option.label }];
		})
	);

	return (
		<div className={styles.container}>
			<Text className={styles.textColor}>{label}</Text>
			<Dropdown
				className={styles.dropdown}
				placeholder="Seleccionar"
				fluid
				selection
				clearable
				options={colorOptions}
				value={effectiveValue}
				onChange={handleChange}
				disabled={disabled}
				multiple={isMultiple}
				defaultUpward={direction === "upward"}
				style={singleModeStyles}
				renderLabel={
					isMultiple
						? (item) => {
								const colorInfo = optionsMap.get(item.value);
								const bgColor = colorInfo ? colorInfo.color : "grey";
								const textColor = isLightColor(bgColor) ? "black" : "white";
								return {
									color: bgColor,
									style: {
										backgroundColor: bgColor,
										color: textColor,
										borderColor: bgColor,
										padding: "5px",
										borderRadius: "5px",
										textAlign: "center",
										display: "inline-block",
									},
									content: colorInfo ? colorInfo.label : item.text,
								};
						  }
						: undefined
				}
			/>
		</div>
	);
};

ColorPickerDropdown.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			color: PropTypes.string,
			label: PropTypes.string.isRequired,
		})
	).isRequired,
	currentColor: PropTypes.string,
	setCurrentColor: PropTypes.func,
	selectedColors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
	setSelectedColors: PropTypes.func,
	isMultiple: PropTypes.bool,
	disabled: PropTypes.bool,
	label: PropTypes.string,
	direction: PropTypes.string,
};

export default ColorPickerDropdown;
